import React from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import { cn } from '../../lib/utils';

interface ProgressProps {
  value?: number;
  max?: number;
  size?: 'sm' | 'default' | 'lg';
  variant?: 'default' | 'success' | 'warning' | 'danger';
  showLabel?: boolean;
  label?: string;
  className?: string;
}

/**
 * Progress - Progress bar component
 * Useful for showing completion status, loading states, etc.
 */
const Progress: React.FC<ProgressProps> = ({
  value = 0,
  max = 100,
  size = 'default',
  variant = 'default',
  showLabel = false,
  label,
  className = '',
  ...props
}) => {
  const { colors } = useTheme();

  // Ensure value is within bounds
  const normalizedValue = Math.min(Math.max(value, 0), max);
  const percentage = (normalizedValue / max) * 100;

  // Size variants
  const sizeClasses = {
    sm: 'h-2',
    default: 'h-3',
    lg: 'h-4',
  };

  // Color variants (use theme status colors where they provide solid fill; fallback to theme surface for track)
  const getVariantClasses = () => {
    switch (variant) {
      case 'success':
        return colors.success;
      case 'warning':
        return colors.warning;
      case 'danger':
        return colors.error;
      default:
        return colors.primary;
    }
  };

  const progressElement = (
    <div
      className={cn(
        'relative w-full overflow-hidden rounded-full',
        colors.surfaceHover,
        sizeClasses[size] || sizeClasses.default,
        className
      )}
      {...props}
    >
      {/* Progress Bar */}
      <div
        className={cn('h-full transition-all duration-500 ease-out rounded-full', getVariantClasses())}
        style={{ width: `${percentage}%` }}
      />

      {/* Optional animated shimmer effect */}
      {percentage > 0 && percentage < 100 && (
        <div
          className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent animate-pulse"
          style={{
            transform: `translateX(-100%)`,
            animation: 'shimmer 2s infinite',
          }}
        />
      )}
    </div>
  );

  // If no label needed, return just the progress bar
  if (!showLabel && !label) {
    return progressElement;
  }

  // Return progress bar with label
  return (
    <div className="space-y-2">
      {/* Label and percentage */}
      <div className="flex items-center justify-between">
        {label && <span className={cn('text-sm font-medium', colors.text)}>{label}</span>}
        {showLabel && <span className={cn('text-sm', colors.textSecondary)}>{Math.round(percentage)}%</span>}
      </div>

      {/* Progress bar */}
      {progressElement}
    </div>
  );
};

interface CircularProgressProps {
  value?: number;
  max?: number;
  size?: number;
  strokeWidth?: number;
  variant?: 'default' | 'success' | 'warning' | 'danger';
  showLabel?: boolean;
  className?: string;
}

/**
 * CircularProgress - Circular progress indicator
 */
const CircularProgress: React.FC<CircularProgressProps> = ({
  value = 0,
  max = 100,
  size = 48,
  strokeWidth = 4,
  variant = 'default',
  showLabel = true,
  className = '',
  ...props
}) => {
  const { colors } = useTheme();

  const normalizedValue = Math.min(Math.max(value, 0), max);
  const percentage = (normalizedValue / max) * 100;

  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const strokeDashoffset = circumference - (percentage / 100) * circumference;

  // Color variants - map theme tokens to stroke (theme gives bg+text, we need stroke so use same semantic)
  const getStrokeColor = () => {
    switch (variant) {
      case 'success':
        return colors.success;
      case 'warning':
        return colors.warning;
      case 'danger':
        return colors.error;
      default:
        return colors.primary;
    }
  };

  return (
    <div className={cn('relative inline-flex items-center justify-center', className)} {...props}>
      <svg width={size} height={size} className="transform -rotate-90">
        {/* Background circle */}
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          stroke="currentColor"
          strokeWidth={strokeWidth}
          fill="none"
          className={colors.surfaceHover}
        />

        {/* Progress circle */}
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          strokeWidth={strokeWidth}
          fill="none"
          strokeDasharray={circumference}
          strokeDashoffset={strokeDashoffset}
          strokeLinecap="round"
          className={cn('transition-all duration-500 ease-out', getStrokeColor())}
        />
      </svg>

      {/* Center label */}
      {showLabel && (
        <div className="absolute inset-0 flex items-center justify-center">
          <span className={cn('text-xs font-semibold', colors.text)}>{Math.round(percentage)}%</span>
        </div>
      )}
    </div>
  );
};

// Add shimmer animation keyframes to document if not already present
if (typeof document !== 'undefined' && !document.getElementById('progress-shimmer-styles')) {
  const style = document.createElement('style');
  style.id = 'progress-shimmer-styles';
  style.textContent = `
    @keyframes shimmer {
      0% { transform: translateX(-100%); }
      100% { transform: translateX(100%); }
    }
  `;
  document.head.appendChild(style);
}

export { Progress, CircularProgress };
export default Progress;

